import type { Report as MReport } from '@/models/report';
import {
  aiDisplayValues,
  fingerprintMeasurement,
  tagsKey,
  type ReportEditBaseline,
} from './reportEditBaseline';

/**
 * Post-save refetch reconcile for the report editor.
 *
 * On a refetch of the *same* report, autosave may still have edits in flight
 * (typed after the PATCH snapshot but before the refetch landed). Adopting the
 * server payload wholesale would clobber those back to the server state, so:
 * rows the user has edited since the last baseline (their fingerprint
 * diverges) are kept; everything else adopts the server payload (picking up
 * server-assigned uuids and any derived values).
 */
export function reconcileReport(
  serverReport: MReport,
  prev: MReport,
  baseline: ReportEditBaseline
): MReport {
  const baselineByCanonical = baseline.measurementByCanonical;
  // Rows without a canonical key would collide in the map — they pass
  // through the reconcile as-is.
  const localByCanonical = new Map(
    prev.measurements
      .filter((m) => !!m.canonicalMeasurement)
      .map((m) => [m.canonicalMeasurement, m])
  );

  const aiValues = aiDisplayValues(prev.aiMeasurements);
  const merged = serverReport.measurements.map((srv) => {
    if (!srv.canonicalMeasurement) return srv;
    const local = localByCanonical.get(srv.canonicalMeasurement);
    if (!local) return srv;
    const dirty =
      baselineByCanonical.get(local.canonicalMeasurement) !==
      fingerprintMeasurement(local, aiValues.get(local.canonicalMeasurement));
    if (!dirty) return srv;
    // Keep the in-flight local edit, but adopt the server-assigned uuid so
    // the next PATCH updates the row instead of creating a duplicate.
    return local.uuid ? local : { ...local, uuid: srv.uuid };
  });

  // Preserve purely-local rows the server doesn't know about yet.
  const serverCanonicals = new Set(
    serverReport.measurements.map((m) => m.canonicalMeasurement)
  );
  prev.measurements.forEach((local) => {
    if (
      local.canonicalMeasurement &&
      !serverCanonicals.has(local.canonicalMeasurement)
    ) {
      merged.push(local);
    }
  });

  // Keep an in-flight tags edit (mirrored into `currentReport.tags` by
  // useReportFlags) instead of clobbering it back to the server copy.
  const tagsDirty = tagsKey(prev.tags ?? []) !== baseline.tagsSorted;

  return {
    ...serverReport,
    measurements: merged,
    tags: tagsDirty ? prev.tags : serverReport.tags,
  };
}
